import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { apiGet, apiPatch, apiPost } from "@/services/api";

type FormState = {
  title: string;
  slug: string;
  brand: string;
  category: string;
  description: string;
  images: string;
  price: string;
  mrp: string;
  discount: string;
  stock: string;
  tags: string;
};

const empty: FormState = {
  title: "",
  slug: "",
  brand: "",
  category: "",
  description: "",
  images: "",
  price: "",
  mrp: "",
  discount: "0",
  stock: "0",
  tags: "",
};

export function VendorProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState<FormState>(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    void apiGet<{ products: (Record<string, unknown> & { id: string })[] }>("/api/vendors/me/products").then((d) => {
      const p = d?.products?.find((x) => x.id === id);
      if (!p) return;
      setForm({
        title: String(p.title ?? ""),
        slug: String(p.slug ?? ""),
        brand: String(p.brand ?? ""),
        category: String(p.category ?? ""),
        description: String(p.description ?? ""),
        images: Array.isArray(p.images) ? (p.images as string[]).join("\n") : "",
        price: String(p.price ?? ""),
        mrp: String(p.mrp ?? ""),
        discount: String(p.discount ?? "0"),
        stock: String(p.stock ?? "0"),
        tags: Array.isArray(p.tags) ? (p.tags as string[]).join(", ") : "",
      });
    });
  }, [id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug,
        brand: form.brand,
        category: form.category,
        description: form.description,
        images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
        price: Number(form.price),
        mrp: Number(form.mrp),
        discount: Number(form.discount),
        stock: Number(form.stock),
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isEdit) await apiPatch(`/api/vendors/me/products/${id}`, payload);
      else await apiPost("/api/vendors/me/products", payload);
      toast.success(isEdit ? "Product updated — sent for review" : "Product submitted for review");
      navigate("/vendor/products");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save product");
    } finally {
      setSaving(false);
    }
  }

  const field = (label: string, key: keyof FormState, props: Record<string, unknown> = {}) => (
    <label className="block text-sm">
      <span className="font-bold text-slate-700 dark:text-slate-200">{label}</span>
      <input
        className="mt-1 w-full rounded border border-slate-300 px-3 py-2 dark:border-white/10 dark:bg-white/5"
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        {...props}
      />
    </label>
  );

  return (
    <form onSubmit={submit} className="amazon-card max-w-2xl space-y-4 p-4 sm:p-6">
      <h2 className="text-xl font-bold">{isEdit ? "Edit product" : "New product"}</h2>
      {field("Title", "title", { required: true, minLength: 3 })}
      {field("Slug", "slug", { required: true, minLength: 3 })}
      {field("Brand", "brand", { required: true })}
      {field("Category", "category", { required: true })}
      <label className="block text-sm">
        <span className="font-bold">Description</span>
        <textarea
          className="mt-1 w-full rounded border border-slate-300 px-3 py-2 dark:border-white/10 dark:bg-white/5"
          rows={4}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          required
          minLength={12}
        />
      </label>
      <label className="block text-sm">
        <span className="font-bold">Image URLs (one per line)</span>
        <textarea
          className="mt-1 w-full rounded border border-slate-300 px-3 py-2 dark:border-white/10 dark:bg-white/5"
          rows={3}
          value={form.images}
          onChange={(e) => setForm((f) => ({ ...f, images: e.target.value }))}
          required
        />
      </label>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        {field("Price", "price", { type: "number", required: true, min: 0 })}
        {field("MRP", "mrp", { type: "number", required: true, min: 0 })}
        {field("Discount %", "discount", { type: "number", min: 0, max: 95 })}
        {field("Stock", "stock", { type: "number", min: 0 })}
      </div>
      {field("Tags (comma separated)", "tags")}
      <button
        disabled={saving}
        className="rounded bg-amazon-orange px-4 py-2 font-bold text-slate-900 disabled:opacity-50"
      >
        {saving ? "Saving…" : isEdit ? "Save changes" : "Submit for review"}
      </button>
    </form>
  );
}
